import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { formatDate } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Eye, Newspaper } from "lucide-react";
import { toast } from "sonner";

export default function NewsManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/news/${id}`);
      toast.success(t("admin.news.toastDeleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="news-manager" className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.news.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.news.title")}</h1>
        </div>
        <Link to="/admin/news/neu" data-testid="new-news-btn">
          <Button className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
            <Plus size={16} className="mr-2" /> {t("admin.news.newBtn")}
          </Button>
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <Newspaper className="mx-auto mb-3" size={22} />
            <p className="text-sm">{t("admin.news.empty")}</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-beige-light border-b border-slate-200">
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.news.colTitle")}</th>
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.news.colStatus")}</th>
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.news.colDate")}</th>
                <th className="px-4 py-3 font-medium text-slate-900 text-right">{t("admin.common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} data-testid={`news-row-${n.id}`} className="border-b border-slate-100 hover:bg-beige-light/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {n.image_path ? (
                        <img src={fileUrl(n.image_path)} alt="" className="w-10 h-10 object-cover rounded-sm border border-slate-200 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 bg-beige rounded-sm border border-slate-200 grid place-items-center text-slate-400 flex-shrink-0">
                          <Newspaper size={14} />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{n.title}</div>
                        <div className="text-xs text-slate-500 line-clamp-1 max-w-md">{n.excerpt}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {n.is_published ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-sm font-medium">{t("admin.news.statusPublished")}</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100 rounded-sm font-medium">{t("admin.news.statusDraft")}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">
                    {formatDate(n.publish_at || n.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {n.is_published && (
                        <Link to={`/news/${n.id}`} target="_blank" rel="noopener noreferrer" data-testid={`news-preview-${n.id}`} className="p-2 hover:bg-slate-100 rounded-sm text-slate-600 hover:text-bordeaux transition-colors">
                          <Eye size={15} />
                        </Link>
                      )}
                      <Link to={`/admin/news/${n.id}`} data-testid={`news-edit-${n.id}`} className="p-2 hover:bg-slate-100 rounded-sm text-slate-600 hover:text-bordeaux transition-colors">
                        <Pencil size={15} />
                      </Link>
                      <button onClick={() => setConfirmId(n.id)} data-testid={`news-delete-${n.id}`} className="p-2 hover:bg-red-50 rounded-sm text-slate-600 hover:text-red-600 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.common.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
